import { createContext, type ReactNode, useContext, useEffect, useState } from "react";

import type { ChoiceTunable, NumberTunable, ToggleTunable } from "../../../shared/tunables";
import { useTunables } from "../../lib/tunables";
import { InfoTip } from "../../ui";

/**
 * The Settings page's building blocks. A setting is a sentence with its
 * values written into it — "Stop a sweep after [50] pages" — so it reads as
 * what it does and takes one line, rather than a label, a box and a Save
 * button apiece. The why sits behind an ⓘ at the end of the sentence. Every
 * control saves on its own; the row it sits in says whether that worked.
 */

type Status = { kind: "saved" } | { kind: "error"; message: string } | null;
const RowStatus = createContext<(s: Status) => void>(() => {});

/** A titled block of rows, and the anchor the section list jumps to. */
export function Group({
	id,
	title,
	intro,
	children,
}: {
	id: string;
	title: string;
	intro?: string;
	children: ReactNode;
}) {
	// scroll-mt clears the app's 48px sticky top bar when the list jumps here.
	return (
		<section id={id} aria-labelledby={`${id}-title`} className="scroll-mt-16">
			<h2 id={`${id}-title`} className="flex items-center text-[13px] font-semibold text-ink">
				{title}
				{intro && <InfoTip label={`About ${title.toLowerCase()}`}>{intro}</InfoTip>}
			</h2>
			<div className="mt-1.5 divide-y divide-hairline rounded-[10px] border border-hairline bg-paper">{children}</div>
		</section>
	);
}

/**
 * One setting. `children` is the sentence (with any Blanks inside it);
 * `control` sits at the right edge for switches and short actions; `help`
 * is the why, behind an ⓘ at the end of the sentence.
 */
export function Row({
	children,
	help,
	control,
	dim = false,
}: {
	children: ReactNode;
	help?: ReactNode;
	control?: ReactNode;
	/** Greyed while a setting it depends on is off; still editable. */
	dim?: boolean;
}) {
	const [status, setStatus] = useState<Status>(null);

	useEffect(() => {
		if (status?.kind !== "saved") return;
		const t = setTimeout(() => setStatus(null), 1800);
		return () => clearTimeout(t);
	}, [status]);

	return (
		<RowStatus.Provider value={setStatus}>
			<div className={`flex items-center gap-3 px-3 py-2 ${dim ? "opacity-60" : ""}`}>
				<div className="min-w-0 flex-1">
					<div className="text-[13px] leading-7 text-ink">
						{children}
						{help && <InfoTip>{help}</InfoTip>}
					</div>
					<p aria-live="polite" className="text-xs">
						{status?.kind === "error" && <span className="text-fault">{status.message}</span>}
						{status?.kind === "saved" && <span className="text-muted">Saved</span>}
					</p>
				</div>
				{control && <div className="flex min-h-7 shrink-0 items-center gap-2">{control}</div>}
			</div>
		</RowStatus.Provider>
	);
}

/** Lets a custom control inside a Row report its own save. */
export function useRowStatus(): (s: Status) => void {
	return useContext(RowStatus);
}

/**
 * A number written into the sentence, saved when you leave it or press
 * Enter. Out-of-range values are refused with the range, not clamped, so
 * what the field shows is what was saved.
 */
export function Blank({ t, label }: { t: NumberTunable; label: string }) {
	const { get, save } = useTunables();
	const report = useRowStatus();
	const saved = get(t);
	const [draft, setDraft] = useState(String(saved));
	const [invalid, setInvalid] = useState(false);

	useEffect(() => {
		setDraft(String(saved));
	}, [saved]);

	const commit = async () => {
		const n = Number(draft.replace(",", "."));
		if (draft.trim() === "" || n === saved) {
			setDraft(String(saved));
			setInvalid(false);
			return;
		}
		if (!Number.isFinite(n) || n < t.min || n > t.max) {
			setInvalid(true);
			report({ kind: "error", message: `Use a number from ${t.min} to ${t.max}.` });
			return;
		}
		const error = await save(t, n);
		setInvalid(!!error);
		report(error ? { kind: "error", message: error } : { kind: "saved" });
	};

	const width = Math.max(String(t.max).length, 2) + 1;
	// A small boxed field sat in the line of text; the width fits the largest
	// allowed value, plus the field's own padding and border.
	return (
		<span className="whitespace-nowrap">
			<input
				type="text"
				inputMode="decimal"
				aria-label={label}
				aria-invalid={invalid}
				value={draft}
				onChange={(e) => {
					setDraft(e.target.value);
					setInvalid(false);
				}}
				onBlur={() => void commit()}
				onKeyDown={(e) => {
					if (e.key === "Enter") (e.target as HTMLInputElement).blur();
					if (e.key === "Escape") {
						setDraft(String(saved));
						setInvalid(false);
					}
				}}
				style={{ width: `calc(${width}ch + 14px)` }}
				className={`mx-1 h-7 rounded-md border bg-ground px-1.5 text-center align-middle text-[13px] font-semibold text-ink hover:border-line pointer-coarse:h-9 ${
					invalid ? "border-fault" : "border-hairline"
				}`}
			/>
			<span className="text-muted">{t.unit}</span>
		</span>
	);
}

/** An on/off switch bound to a toggle tunable. */
export function Switch({ t, label }: { t: ToggleTunable; label: string }) {
	const { get, save } = useTunables();
	const report = useRowStatus();
	const on = get(t);
	const [busy, setBusy] = useState(false);

	const flip = async () => {
		setBusy(true);
		const error = await save(t, !on);
		setBusy(false);
		report(error ? { kind: "error", message: error } : { kind: "saved" });
	};

	// The track is small; the invisible ::before widens the hit area, to a
	// finger's worth on touch, without growing the row.
	return (
		<button
			type="button"
			role="switch"
			aria-checked={on}
			aria-label={label}
			disabled={busy}
			onClick={() => void flip()}
			className={`relative inline-flex h-5 w-8 shrink-0 items-center rounded-full border transition-colors before:absolute before:-inset-1.5 before:content-[''] disabled:opacity-60 pointer-coarse:before:-inset-3 ${
				on ? "border-ink bg-ink" : "border-line bg-ground"
			}`}
		>
			<span
				aria-hidden
				className={`inline-block size-3.5 rounded-full transition-transform ${
					on ? "translate-x-[13px] bg-paper" : "translate-x-[2px] bg-muted"
				}`}
			/>
		</button>
	);
}

/** One of a few named choices, as a segmented control. */
export function Choice<T extends string>({
	t,
	label,
	labels,
}: {
	t: ChoiceTunable<T>;
	label: string;
	labels: Record<T, string>;
}) {
	const { get, save } = useTunables();
	const report = useRowStatus();
	const current = get(t);

	const pick = async (value: T) => {
		if (value === current) return;
		const error = await save(t, value);
		report(error ? { kind: "error", message: error } : { kind: "saved" });
	};

	return (
		<span
			role="radiogroup"
			aria-label={label}
			className="mt-1 inline-flex flex-wrap gap-0.5 rounded-lg border border-hairline bg-ground p-0.5"
		>
			{t.options.map((o) => (
				<button
					key={o}
					type="button"
					role="radio"
					aria-checked={o === current}
					onClick={() => void pick(o)}
					className={`h-7 rounded-md px-2.5 text-xs font-medium pointer-coarse:h-10 ${
						o === current ? "bg-ink text-ground" : "text-muted hover:bg-raised hover:text-ink"
					}`}
				>
					{labels[o]}
				</button>
			))}
		</span>
	);
}
